import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
} from "body-scroll-lock";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { CSSTransition } from "react-transition-group";
import { t } from "ttag";

import iconArrowLeftBlueAlternate from "../../../../img/icons/arrow-left-blue--alternate.svg";
import iconArrowRightBlue from "../../../../img/icons/arrow_right_blue.svg";
import { useAppSelector } from "../../../store/reducers";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { format } from "../../../utils/format";
import { focusElement, trapFocus } from "../../../utils/keyboardFocus";
import { slugify } from "../../../utils/text";
import { PLCProductOptionSelector } from "../PLCProductOptionSelector";
import { PLCProductOptionSelectorCurrentValue } from "../PLCProductOptionSelectorCurrentValue";
import { defaults } from "../defaults";
import { IPLCOptionPanel, IPLCProductCategorySelector } from "../models";
import { upgradedVariantPriceSelector } from "../selectors";

// Load styles
import "./index.scss";

const BEM_NAME = "plc-option-panel";

const handleAfterModalOpen = (
    triggerElem: React.RefObject<HTMLButtonElement | null>,
    closeBtnElem: React.RefObject<HTMLButtonElement | null>,
    modalElem: React.RefObject<HTMLDivElement | null>,
): void => {
    if (!triggerElem.current || !modalElem.current) {
        return;
    }

    if (closeBtnElem.current) {
        focusElement(closeBtnElem.current);
    }
    trapFocus(modalElem.current, { includeExplicitTabindex: true });
};

export const PLCOptionPanel = (
    props: Immutable<{
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
        panel: IPLCOptionPanel;
    }>,
): React.ReactNode => {
    const [isOpen, setIsOpen] = React.useState(false);
    const triggerElem = React.useRef<HTMLButtonElement | null>(null);
    const closeBtnElem = React.useRef<HTMLButtonElement | null>(null);
    const modalElem = React.useRef<HTMLDivElement | null>(null);
    const previousIsOpenRef = React.useRef(false);

    const price = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return upgradedVariantPriceSelector(state);
    });

    const onOpenModal = () => {
        setIsOpen(true);
    };

    const onRequestClose = () => {
        // `enableBodyScroll` should run before setState `isPanelOpen: false`,
        // so inline style `position: fixed` from `<body>` will be removed first before removing `.configurator-panel-open` from `<body>`.
        // This way can avoid the issue of the page jumping to the top when panel is opened/closed
        if (modalElem.current) {
            enableBodyScroll(modalElem.current);
        }

        setIsOpen(false);
    };

    // Handle modal open side effects
    React.useEffect(() => {
        if (isOpen && modalElem.current) {
            disableBodyScroll(modalElem.current);
            handleAfterModalOpen(triggerElem, closeBtnElem, modalElem);
        }
    }, [isOpen]);

    // Handle focus return on modal close
    React.useEffect(() => {
        const wasOpen = previousIsOpenRef.current;
        if (!isOpen && wasOpen && triggerElem.current) {
            focusElement(triggerElem.current);
        }
        previousIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        };
    }, []);

    const panelName = props.panel.value.short_name;
    const panelNameSlugged = slugify(panelName);
    const formattedPrice = price
        ? getProductPrice(price, {
              priceType: PriceType.COSMETIC_EXCL_TAX,
              includePostDiscountAddons: true,
              quantity: price.quantity,
          })
        : null;

    return (
        <div className={BEM_NAME}>
            <div className={`${BEM_NAME}__trigger`}>
                <div className={`${BEM_NAME}__label`}>{panelName}</div>
                <button onClick={onOpenModal} ref={triggerElem}>
                    <PLCProductOptionSelectorCurrentValue
                        selectors={props.panel.value.selectors}
                    />
                    <SVG src={iconArrowRightBlue} />
                </button>
            </div>
            <CSSTransition
                key="plc-option-panel"
                in={isOpen}
                timeout={100}
                classNames="plc-option-panel__window-"
                unmountOnExit={true}
                nodeRef={modalElem}
            >
                <div
                    aria-modal="true"
                    className={`${BEM_NAME}__window`}
                    ref={modalElem}
                    role="dialog"
                >
                    <header>
                        <button
                            aria-label={panelName}
                            onClick={onRequestClose}
                            ref={closeBtnElem}
                        >
                            <SVG src={iconArrowLeftBlueAlternate} />
                            {panelName}
                        </button>
                        <div className={`${BEM_NAME}__current-price`}>
                            {formattedPrice ? format.money(formattedPrice) : ""}
                        </div>
                    </header>
                    <div
                        className={`${BEM_NAME}__content ${BEM_NAME}__content--${panelNameSlugged}`}
                    >
                        {props.panel.value.selectors.map((selector) => (
                            <PLCProductOptionSelector
                                key={selector.id}
                                categorySelectors={props.categorySelectors}
                                optionPanels={props.optionPanels}
                                selector={selector}
                            />
                        ))}
                    </div>
                    <div
                        className={`${BEM_NAME}__content ${BEM_NAME}__content--button`}
                    >
                        <button
                            className={`${BEM_NAME}__done-button button button--primary`}
                            aria-label={t`Done`}
                            onClick={onRequestClose}
                        >
                            {t`Done`}
                        </button>
                    </div>
                </div>
            </CSSTransition>
        </div>
    );
};
